import React, { memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { Checkbox, FormControlLabel, Tooltip, Typography } from '@mui/material'
import type { InternalPermissionGroupView } from 'src/interfaces/internalAccess'
import { humanizeSnakeCase } from 'src/utils/humanizeSnakeCase'
import {
  resolveGroupSelectionState,
  toggleGroupPermissions,
  togglePermission,
} from 'src/utils/internalAccess/permissionSelection'

interface Props {
  group: InternalPermissionGroupView
  selected: string[]
  /** What the editor holds — the backend refuses to let anyone grant more than that. */
  grantable: string[]
  disabled: boolean
  onChange(permissions: string[]): void
}

const NOT_GRANTABLE_HINT = "You can't grant a permission you don't hold."

const InternalPermissionGroupComponent = ({
  group,
  selected,
  grantable,
  disabled,
  onChange,
}: Props) => {
  const selectedCount = useMemo(() => {
    return group.permissions.filter((permission) => {
      return selected.includes(permission)
    }).length
  }, [group.permissions, selected])

  const hasGrantable = useMemo(() => {
    return group.permissions.some((permission) => {
      return grantable.includes(permission)
    })
  }, [group.permissions, grantable])

  // Over the grantable subset, the same one the group toggle acts on.
  const { isAllSelected, isPartiallySelected } = useMemo(() => {
    return resolveGroupSelectionState(group.permissions, selected, grantable)
  }, [grantable, group.permissions, selected])

  const handleGroupToggle = useCallback(() => {
    onChange(toggleGroupPermissions(selected, group.permissions, grantable))
  }, [grantable, group.permissions, onChange, selected])

  const handleToggle = useCallback(
    (permission: string) => {
      onChange(togglePermission(selected, permission))
    },
    [onChange, selected],
  )

  return (
    <div
      css={tw`rounded-lg border border-solid border-shades-100 p-4`}
      data-testid={`internal-permission-group-${group.label}`}
    >
      <div css={tw`mb-2 flex flex-row items-center justify-between gap-4`}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={isAllSelected}
              indeterminate={isPartiallySelected}
              disabled={disabled || !hasGrantable}
              onChange={handleGroupToggle}
            />
          }
          label={
            <Typography variant="body2" css={tw`font-semibold`}>
              {group.label}
            </Typography>
          }
        />
        <Typography variant="body2" css={tw`text-xs text-shades-500`}>
          {selectedCount} / {group.permissions.length}
        </Typography>
      </div>
      <div css={tw`grid grid-cols-1 gap-x-4 md:grid-cols-2`}>
        {group.permissions.map((permission) => {
          const isGrantable = grantable.includes(permission)
          const control = (
            <FormControlLabel
              css={tw`flex`}
              control={
                <Checkbox
                  size="small"
                  checked={selected.includes(permission)}
                  disabled={disabled || !isGrantable}
                  onChange={() => {
                    handleToggle(permission)
                  }}
                  inputProps={
                    {
                      'data-testid': `internal-role-permission-${permission}`,
                    } as React.InputHTMLAttributes<HTMLInputElement>
                  }
                />
              }
              label={<Typography variant="body2">{humanizeSnakeCase(permission)}</Typography>}
            />
          )

          if (disabled || isGrantable) {
            return <div key={permission}>{control}</div>
          }

          return (
            <Tooltip key={permission} title={NOT_GRANTABLE_HINT} placement="top-start">
              <span>{control}</span>
            </Tooltip>
          )
        })}
      </div>
    </div>
  )
}

export const InternalPermissionGroup = memo(InternalPermissionGroupComponent)
